import { ComponentType, ReactNode, useEffect } from 'react'

import { InlineFontFamily, PreloadFonts } from 'hhgComponentsV2/fonts'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import { useRouterExtend } from 'hooks/useRouterExtend'
import { DEFAULT_FONT_LIST, MAPPED_FONT_LIST } from 'interfaces/constants/fonts'
import { LOCALE } from 'interfaces/types'

// @ts-ignore
import battambangFontCss from '!raw-loader!../public/fonts/battambang/font.css'
// @ts-ignore
import interFontCss from '!raw-loader!../public/fonts/inter/font.css'
// @ts-ignore
import notoSansThaiFontCss from '!raw-loader!../public/fonts/notoSansThai/font.css'

const AppLegacy = dynamic(() => import('components/pages/_appLegacy'))

/**
 * TODO: Need to find the better solution for this,
 * .. This method just load the SCSS file on client-side only
 * Component to handle dynamic SCSS import
 */
const DynamicStylesLoader = () => {
  useEffect(() => {
    // Dynamic import on client-side only
    try {
      // @ts-ignore - SCSS files don't have type declarations
      import('../styles/base.scss')
    } catch (error) {
      console.warn('Failed to load base.scss:', error)
    }
  }, [])

  return null
}

export type NextPageWithLayout = NextPage<{
  err?: Error
}> & {
  LayoutComponent?: ComponentType<{
    children: ReactNode
    pageProps?: AppProps['pageProps']
  }>
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  err?: Error
}

export default function HHGDiscover({
  Component,
  pageProps,
  err,
  ...appProps
}: AppPropsWithLayout) {
  const { page: pageName } = pageProps
  const articleDetailPage = pageName === 'article'

  const { locale } = useRouterExtend()
  console.log('[👀 HHG Tracking] _app.tsx', {
    pageName,
    articleDetailPage,
    locale,
  })

  // Rebuild Article Detail App
  if (articleDetailPage) {
    return (
      <>
        <Head>
          {/* Handle Fonts */}
          <PreloadFonts locale={locale} />
          <InlineFontFamily locale={locale} />

          <SetupFirstAdCmd locale={locale} />
        </Head>
        <Component {...pageProps} err={err} />
      </>
    )
  }

  // Legacy App
  return (
    <>
      <Head>
        {(MAPPED_FONT_LIST[locale] || DEFAULT_FONT_LIST).map((font, index) => (
          <link
            key={index}
            rel="preload"
            href={font}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        ))}

        {locale === LOCALE.Cambodia ? (
          <style
            dangerouslySetInnerHTML={{
              __html: battambangFontCss,
            }}
          />
        ) : locale === LOCALE.Thailand ? (
          <style
            dangerouslySetInnerHTML={{
              __html: notoSansThaiFontCss,
            }}
          />
        ) : (
          <style
            dangerouslySetInnerHTML={{
              __html: interFontCss,
            }}
          />
        )}

        <script
          async
          id="datalayer-init"
          key="datalayer-init"
          dangerouslySetInnerHTML={{
            __html: `
                    window.dataLayer = window.dataLayer || [];
                  `,
          }}
        />
      </Head>
      <DynamicStylesLoader />
      <AppLegacy
        Component={Component}
        pageProps={pageProps}
        err={err}
        {...appProps}
      />
    </>
  )
}

function SetupFirstAdCmd({ locale }: { locale: LOCALE }) {
  const isDisabledAd = false
  const isEnableSingleRequest = false
  return (
    <script
      async
      dangerouslySetInnerHTML={{
        __html: `
        window.googletag = window.googletag || { cmd: [] };

        googletag.cmd.push(function () {
          googletag.pubads().collapseEmptyDivs(true);

          /* Let use "googletag.pubads().refresh()" if you use "disableInitialLoad()" */
          ${isDisabledAd ? 'googletag.pubads().disableInitialLoad();' : ''}

          /* "enableSingleRequest" --> Request all ad slots in one call */
          ${
            isEnableSingleRequest
              ? 'googletag.pubads().enableSingleRequest();'
              : ''
          }

          googletag.pubads().enableLazyLoad({
            fetchMarginPercent: 300,
            renderMarginPercent: ${locale === LOCALE.Vietnam ? 50 : 30},
            mobileScaling: 2.0,
          });

          googletag.enableServices();

          if (window.location.hostname.startsWith('demo.')) {
            googletag.pubads().setTargeting("demo",["yes"]);
          }
            
          if (window.location.pathname.startsWith('/preview/')) {
            googletag.pubads().setTargeting("preview",["yes"]);
          }
        });
      `,
      }}
    />
  )
}
